<template>
    <ProductView :product="product" :block-settings="blockSettings" />
</template>

<script setup lang="ts">
import { Product } from '~/types/product';
import ProductView from '~/components/page-building/product-view/ProductView.vue';
import { imageFromStoryblokAsset, richTextToHtml } from '~/utils/storyblok';
import { useProductStructuredData } from '~/composables/useStructuredData';
import {
    linkFromStoryblokLinkBlock,
    urlFromStoryblokLinkField,
} from '~/utils/helpers';

const props = defineProps<{
    story: any;
}>();

const block = computed<any>(() => {
    return props.story.content;
});

const nextBlock = computed<any>(() => {
    return props.story.content.blocks && props.story.content.blocks.length
        ? props.story.content.blocks[0]
        : null;
});

const product = computed<Product>(() => {
    const image = imageFromStoryblokAsset(block.value.image);

    const tags = block.value.tags
        ? block.value.tags.map((tag: any) => tag.content.label)
        : [];

    const variations = block.value.variations
        ? block.value.variations.map((story: any) => {
              const variation = story.content;

              return {
                  id: variation.database_product?.id,
                  sku: variation.database_product?.sku,
                  image: imageFromStoryblokAsset(variation.image),
                  name: variation.name,
                  description: richTextToHtml(variation.description),
                  details: variation.details
                      ? variation.details.map((detail: any) => {
                            return {
                                label: detail.label,
                                content: richTextToHtml(detail.content),
                            };
                        })
                      : [],
                  vEditable: variation,
              };
          })
        : [];

    const descriptionDetails = block.value.description_details
        ? block.value.description_details.map((descriptionDetail: any) => {
              return {
                  icon: imageFromStoryblokAsset(descriptionDetail.icon),
                  detail: descriptionDetail.detail,
                  vEditable: descriptionDetail,
              };
          })
        : [];

    const relatedItems = block.value.related_items
        ? block.value.related_items.map((relatedItem: any) => {
              return {
                  image: imageFromStoryblokAsset(relatedItem.image),
                  title: relatedItem.title,
                  content: richTextToHtml(relatedItem.content),
                  link: linkFromStoryblokLinkBlock(relatedItem.link[0]),
                  vEditable: relatedItem,
              };
          })
        : [];

    const linkedProductUrl = block.value.linked_product_url
        ? urlFromStoryblokLinkField(block.value.linked_product_url)
        : null;

    return {
        image,
        title: block.value.title,
        subtitle: block.value.subtitle,
        description: richTextToHtml(block.value.description),
        descriptionDetails,
        shortDescription: richTextToHtml(block.value.short_description),
        tags,
        configurator: block.value.configurator || '',
        variationDefaultDescription: richTextToHtml(
            block.value.variation_default_description,
        ),
        linkedProductUrl,
        isAvailableInDesignLab: !!block.value.linked_design_lab_product,
        variations,
        relatedItems,
        vEditable: block.value,
    };
});

const structuredData = useProductStructuredData(product.value);

useServerHead(() => ({
    script: structuredData,
}));

const blockSettings = useStoryblokBlock(block, nextBlock).settings;
</script>
